import type { ContratCxpiDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import { isNotNullUndefinedOrEmptyString } from '@/features/shared/tools'
import type { NaturesFichiersDemandeCallback } from '@/features/domaine/regles-akto/shared'

/* Natures fichiers CxPI */
export const ContratTypeLettreMission = 'ContratTypeLettreMission' as const
export const ReconnaissanceTravailleurHandicapé = 'ReconnaissanteQTH' as const
export const CharteEngagementMissionJeune = 'CharteEngagementMissionJeune' as const
// export const ProgrammeFormationExterne = 'ProgrammeFormationExterne' as const

/* Natures fichiers DRF */
export const Convention = 'Convention' as const
export const ConventionBilanEvaluation = 'ConventionBilanEvaluation' as const
export const ConventionBilanPositionnement = 'ConventionBilanPositionnement' as const
export const ProtocoleFormationAFEST = 'ProtocoleFormationAFEST' as const
export const Programme = 'Programme' as const
export const EngagementParties = 'EngagementParties' as const

export function getStatModule(demande: ContratCxpiDTO) {
  return (demande.formation?.modules ?? []).reduce<{
    nb: number
    nbExternes: number
    existBilanEval: boolean
    existBilanPos: boolean
    nbAFEST: number
    nbEntrepriseUtilisatrice: number
  }>(
    (stat, module) => {
      stat.nb++
      stat.nbExternes += module.typeModule === 'Externe' ? 1 : 0
      stat.existBilanEval = stat.existBilanEval || module.typeModule === 'BilanEvaluation'
      stat.existBilanPos = stat.existBilanPos || module.typeModule === 'BilanPositionnement'
      stat.nbAFEST += module.modalitePedagogique === 'Afest' ? 1 : 0
      stat.nbEntrepriseUtilisatrice += module.formationRealiseeDansEntreprise === true ? 1 : 0
      return stat
    },
    {
      nb: 0,
      nbExternes: 0,
      existBilanEval: false,
      existBilanPos: false,
      nbAFEST: 0,
      nbEntrepriseUtilisatrice: 0,
    },
  )
}

export const getNaturesFichiersCxpi: NaturesFichiersDemandeCallback<ContratCxpiDTO> = (demande, _typeContext) => {
  const statModule = getStatModule(demande)

  if (demande.typeDemande === 'Formation') return getFichiersDemandeFormation(demande, statModule)
  if ('code' in demande && isNotNullUndefinedOrEmptyString(demande.code))
    return getFichiersDossierDrf(demande, statModule)
  else return getFichiersDemandeDrf(demande, statModule)
}

function getFichiersDemandeFormation(demande: ContratCxpiDTO, _statModule: ReturnType<typeof getStatModule>) {
  const obligatoires: string[] = [ContratTypeLettreMission]
  const facultatifs: string[] = ['Autre']
  const facultatifsDefaut: string[] = []
  const nombreFichiersMaximum: Record<string, number> = {}
  const nombreFichiersMinimum: Record<string, number> = {}

  if (demande.salarie?.beneficieReconnaissanceTravailleurHandicape) {
    obligatoires.push(ReconnaissanceTravailleurHandicapé)
  }

  if (demande.salarie?.contratPacea || demande.salarie?.contratEngagementJeune) {
    obligatoires.push(CharteEngagementMissionJeune)
  }

  return {
    obligatoires,
    facultatifs,
    facultatifsDefaut,
    nombreFichiersMaximum,
    nombreFichiersMinimum,
  }
}

function getFichiersDemandeDrf(_demande: ContratCxpiDTO, statModule: ReturnType<typeof getStatModule>) {
  const obligatoires: string[] = []
  const facultatifs: string[] = [Convention, 'Autre']
  const facultatifsDefaut: string[] = []
  const nombreFichiersMaximum: Record<string, number> = {}
  if (statModule.nbEntrepriseUtilisatrice > 0) {
    obligatoires.push(Programme)
    obligatoires.push(EngagementParties)
  } else {
    facultatifs.push(Programme)
  }

  if (statModule.nbAFEST > 0) {
    facultatifs.push(ProtocoleFormationAFEST)
  }

  if (statModule.existBilanPos) {
    facultatifs.push(ConventionBilanPositionnement)
  }

  if (statModule.existBilanEval) {
    facultatifs.push(ConventionBilanEvaluation)
  }

  return {
    obligatoires,
    facultatifs,
    facultatifsDefaut,
    nombreFichiersMaximum,
    nombreFichiersMinimum: {
      [Programme]: statModule.nbExternes,
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: statModule.nbAFEST } : {}),
    },
    messagesAvertissement: {
      [Programme]: '(un programme par module externe)',
      [Convention]: '(une convention par module externe)',
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: '(un protocole AFEST par module externe)' } : {}),
    },
  }
}

function getFichiersDossierDrf(_demande: ContratCxpiDTO, statModule: ReturnType<typeof getStatModule>) {
  const obligatoires: string[] = [Convention, Programme]

  if (statModule.nbEntrepriseUtilisatrice > 0) {
    obligatoires.push(EngagementParties)
  }

  if (statModule.nbAFEST > 0) {
    obligatoires.push(ProtocoleFormationAFEST)
  }

  if (statModule.existBilanPos) {
    obligatoires.push(ConventionBilanPositionnement)
  }

  if (statModule.existBilanEval) {
    obligatoires.push(ConventionBilanEvaluation)
  }

  return {
    obligatoires,
    facultatifs: [],
    facultatifsDefaut: [],
    nombreFichiersMaximum: {},
    nombreFichiersMinimum: {
      [Convention]: statModule.nbExternes,
      [Programme]: statModule.nbExternes,
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: statModule.nbAFEST } : {}),
    },
    messagesAvertissement: {
      [Programme]: '(un programme par module externe)',
      [Convention]: '(une convention par module externe)',
      ...(statModule.nbAFEST > 0 ? { [ProtocoleFormationAFEST]: '(un protocole AFEST par module externe)' } : {}),
    },
  }
}

